import React, { useState, useEffect } from 'react';
import { CreditCard, Building2, Copy, Check, Link2, Plus, Eye, EyeOff, Send, QrCode, Shield, ShieldCheck, Clock, Crown, ChevronRight, Globe, Wallet, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useBankAccount, useCards, useKycStatus, useWallets } from '../hooks/useData';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Currency options
const CURRENCIES = [
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
];

// Card network icons (SVG paths)
const CARD_NETWORKS = [
  { name: 'Visa', color: '#1A1F71', textColor: 'white' },
  { name: 'Mastercard', color: '#EB001B', textColor: 'white' },
  { name: 'Maestro', color: '#0099DF', textColor: 'white' },
  { name: 'Amex', color: '#006FCF', textColor: 'white' },
  { name: 'UnionPay', color: '#D32029', textColor: 'white' },
];

// Crypto symbols to CoinGecko IDs mapping
const CRYPTO_IDS = {
  'ETH': 'ethereum',
  'BTC': 'bitcoin',
  'SOL': 'solana',
  'MATIC': 'matic-network',
  'BNB': 'binancecoin',
  'AVAX': 'avalanche-2',
  'ARB': 'arbitrum',
  'USDT': 'tether',
  'USDC': 'usd-coin',
};

// Network name to symbol mapping
const NETWORK_TO_SYMBOL = {
  'ethereum': 'ETH',
  'bitcoin': 'BTC',
  'solana': 'SOL',
  'polygon': 'MATIC',
  'bsc': 'BNB',
  'avalanche': 'AVAX',
  'arbitrum': 'ARB',
};

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  
  // Cached data
  const { bankAccount } = useBankAccount(user?.email);
  const { cards } = useCards(user?.email);
  const { kycStatus } = useKycStatus();
  const { wallets: cryptoWallets } = useWallets();
  
  const [copiedField, setCopiedField] = useState(null);
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDescription, setPaymentDescription] = useState('');
  const [generatedLink, setGeneratedLink] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);
  const [livePrices, setLivePrices] = useState({});
  const [pricesLoading, setPricesLoading] = useState(true);

  // Fetch live crypto prices from CoinGecko
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await axios.get(`${API}/api/trading/prices/live`);
        const pricesArray = response.data.prices || [];
        const pricesObj = {};
        pricesArray.forEach(coin => {
          pricesObj[coin.symbol?.toUpperCase()] = coin;
        });
        setLivePrices(pricesObj);
        setPricesLoading(false);
      } catch (error) {
        console.error('Error fetching prices:', error);
        setPricesLoading(false);
      }
    };
    
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Calculate wallet value based on live prices
  const getWalletValue = (wallet) => {
    const networkName = wallet.network?.toLowerCase();
    const symbol = wallet.symbol?.toUpperCase() || NETWORK_TO_SYMBOL[networkName] || networkName?.toUpperCase();
    const priceData = livePrices[symbol];
    if (priceData && wallet.balance) {
      return wallet.balance * (priceData.price || priceData.current_price || 0);
    }
    return 0;
  };

  // Get price change for a crypto
  const getPriceChange = (symbol) => {
    const priceData = livePrices[symbol?.toUpperCase()];
    return priceData?.change_24h || priceData?.price_change_percentage_24h || 0;
  };

  // Get current price for a crypto
  const getCurrentPrice = (symbol) => {
    const priceData = livePrices[symbol?.toUpperCase()];
    return priceData?.price || priceData?.current_price || 0;
  };

  // Theme colors
  const cardBg = isDark ? 'bg-[#111111]' : 'bg-white';
  const cardBorder = isDark ? 'border-white/10' : 'border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/60' : 'text-gray-600';
  const textSubtle = isDark ? 'text-white/40' : 'text-gray-400';
  const inputBg = isDark ? 'bg-white/5' : 'bg-gray-50';

  // Get current currency details
  const currentCurrency = CURRENCIES.find(c => c.code === selectedCurrency) || CURRENCIES[0];

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const generatePaymentLink = () => {
    if (!paymentAmount) {
      toast.error('Please enter an amount');
      return;
    }
    const linkId = Math.random().toString(36).substring(2, 10);
    const link = `https://pay.onerik.com/${linkId}?amount=${paymentAmount}&desc=${encodeURIComponent(paymentDescription)}`;
    setGeneratedLink(link);
    toast.success('Payment link created!');
  };

  // KYC Badge
  const KycBadge = () => {
    const isVerified = kycStatus?.kyc_verified || kycStatus?.status === 'GREEN';
    const isPending = kycStatus?.status === 'pending' || kycStatus?.status === 'init';
    
    if (isVerified) {
      return (
        <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-3 py-1">
          <ShieldCheck className="w-4 h-4 text-green-500" />
          <span className="text-green-400 text-sm font-medium">Verified</span>
        </div>
      );
    }
    if (isPending) {
      return (
        <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-3 py-1">
          <Clock className="w-4 h-4 text-yellow-500" />
          <span className="text-yellow-400 text-sm font-medium">KYC Pending</span>
        </div>
      );
    }
    return (
      <button 
        onClick={() => navigate('/get-started')}
        className="flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-3 py-1 hover:bg-purple-500/20 transition-all"
      >
        <Shield className="w-4 h-4 text-purple-400" />
        <span className="text-purple-400 text-sm font-medium">Complete KYC</span>
      </button>
    );
  };

  // Mock virtual card if none exists
  const virtualCard = cards?.[0] || {
    card_number: '4532 •••• •••• 8901',
    card_number_full: '4532 7891 2345 8901',
    expiry: '12/28',
    cvv: '***',
    type: 'virtual',
    status: 'active'
  };

  const totalBalance = (bankAccount?.balance || 0) + (cryptoWallets?.reduce((sum, w) => sum + (w.value || 0), 0) || 0);

  return (
    <div data-testid="dashboard-page" className="min-h-[calc(100vh-120px)] py-4 px-3 md:px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className={`text-xl md:text-2xl font-bold ${textPrimary}`}>
              Welcome back, {user?.full_name?.split(' ')[0] || 'User'}
            </h1>
            <p className={`${textMuted} text-sm`}>Your banking overview</p>
          </div>
          <KycBadge />
        </div>

        {/* Main Grid - 3 columns on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          
          {/* Column 1: Bank Account Details */}
          <div className={`${cardBg} border ${cardBorder} rounded-2xl p-5`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-500" />
                <h2 className={`font-semibold ${textPrimary}`}>Bank Account</h2>
              </div>
              {/* Currency Selector */}
              <div className="relative">
                <button 
                  onClick={() => setShowCurrencyPicker(!showCurrencyPicker)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${inputBg} border ${cardBorder} hover:border-purple-500/50 transition-colors`}
                >
                  <span>{currentCurrency.flag}</span>
                  <span className={`text-sm font-medium ${textPrimary}`}>{selectedCurrency}</span>
                  <ChevronDown className={`w-4 h-4 ${textMuted} ${showCurrencyPicker ? 'rotate-180' : ''} transition-transform`} />
                </button>
                {showCurrencyPicker && (
                  <div className={`absolute right-0 top-full mt-1 ${cardBg} border ${cardBorder} rounded-xl overflow-hidden shadow-xl z-10 min-w-[140px]`}>
                    {CURRENCIES.map((curr) => (
                      <button
                        key={curr.code}
                        onClick={() => {
                          setSelectedCurrency(curr.code);
                          setShowCurrencyPicker(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 ${
                          selectedCurrency === curr.code 
                            ? 'bg-purple-500/10' 
                            : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                        } transition-colors`}
                      >
                        <span>{curr.flag}</span>
                        <span className={`text-sm ${textPrimary}`}>{curr.code}</span>
                        {selectedCurrency === curr.code && (
                          <Check className="w-4 h-4 text-purple-500 ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Total Balance */}
            <div className="mb-5">
              <p className={`text-xs ${textMuted} mb-1`}>Total Balance</p>
              <p className={`text-3xl font-bold ${textPrimary}`}>
                {currentCurrency.symbol}{totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>

            {/* Account Details */}
            <div className="space-y-3">
              {/* Account Holder */}
              <div className={`${inputBg} rounded-xl p-3`}>
                <p className={`text-xs ${textMuted} mb-1`}>Account Holder</p>
                <p className={`text-sm font-medium ${textPrimary}`}>{user?.full_name || 'Account Holder'}</p>
              </div>

              {/* IBAN */}
              <div className={`${inputBg} rounded-xl p-3`}>
                <div className="flex items-center justify-between">
                  <p className={`text-xs ${textMuted}`}>IBAN</p>
                  <button 
                    onClick={() => copyToClipboard(bankAccount?.iban || 'GB10 ONRK 0963 1518 6791 88', 'iban')}
                    className={`${textMuted} hover:${textPrimary} transition-colors`}
                  >
                    {copiedField === 'iban' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className={`text-sm font-mono font-medium ${textPrimary} mt-1`}>
                  {bankAccount?.iban || 'GB10 ONRK 0963 1518 6791 88'}
                </p>
              </div>

              {/* Sort Code & Account Number */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`${inputBg} rounded-xl p-3`}>
                  <div className="flex items-center justify-between">
                    <p className={`text-xs ${textMuted}`}>Sort Code</p>
                    <button 
                      onClick={() => copyToClipboard('09-63-15', 'sortcode')}
                      className={`${textMuted} hover:${textPrimary} transition-colors`}
                    >
                      {copiedField === 'sortcode' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                    </button>
                  </div>
                  <p className={`text-sm font-mono font-medium ${textPrimary} mt-1`}>09-63-15</p>
                </div>
                <div className={`${inputBg} rounded-xl p-3`}>
                  <div className="flex items-center justify-between">
                    <p className={`text-xs ${textMuted}`}>Account No.</p>
                    <button 
                      onClick={() => copyToClipboard('18679188', 'accno')}
                      className={`${textMuted} hover:${textPrimary} transition-colors`}
                    >
                      {copiedField === 'accno' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                    </button>
                  </div>
                  <p className={`text-sm font-mono font-medium ${textPrimary} mt-1`}>18679188</p>
                </div>
              </div>

              {/* SWIFT/BIC */}
              <div className={`${inputBg} rounded-xl p-3`}>
                <div className="flex items-center justify-between">
                  <p className={`text-xs ${textMuted}`}>SWIFT/BIC</p>
                  <button 
                    onClick={() => copyToClipboard('ONRKGB3C', 'swift')}
                    className={`${textMuted} hover:${textPrimary} transition-colors`}
                  >
                    {copiedField === 'swift' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className={`text-sm font-mono font-medium ${textPrimary} mt-1`}>ONRKGB3C</p>
              </div>
            </div>
          </div>

          {/* Column 2: Virtual Card + Payment Link */}
          <div className="space-y-4">
            {/* Virtual Card */}
            <div className={`${cardBg} border ${cardBorder} rounded-2xl p-5`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-purple-500" />
                  <h2 className={`font-semibold ${textPrimary}`}>Virtual Card</h2>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${virtualCard.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                  {virtualCard.status === 'active' ? 'Active' : 'Pending'}
                </span>
              </div>

              {/* Card Visual */}
              <div className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-blue-700 rounded-2xl p-5 text-white mb-4 overflow-hidden">
                {/* Card chip */}
                <div className="absolute top-5 left-5 w-10 h-7 bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-md opacity-80"></div>
                {/* Contactless */}
                <div className="absolute top-5 right-5">
                  <svg className="w-8 h-8 text-white/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6" />
                    <path d="M11.5 16.5a4.5 4.5 0 004-6c-.5-1.5-1-2.5-1.5-3.5-1-2-.5-4 1.5-6" />
                  </svg>
                </div>
                
                <div className="mt-10">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-lg font-mono tracking-wider">
                      {showCardNumber ? virtualCard.card_number_full : virtualCard.card_number}
                    </p>
                    <button onClick={() => setShowCardNumber(!showCardNumber)} className="text-white/60 hover:text-white">
                      {showCardNumber ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-xs text-white/60">Card Holder</p>
                      <p className="text-sm font-medium">{user?.full_name?.toUpperCase() || 'CARD HOLDER'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/60">Expires</p>
                      <p className="text-sm font-medium">{virtualCard.expiry}</p>
                    </div>
                    <div className="text-2xl font-bold opacity-80">VISA</div>
                  </div>
                </div>
              </div>

              {/* Card Actions */}
              <div className="grid grid-cols-3 gap-2">
                <button className={`${inputBg} rounded-xl p-3 text-center hover:bg-purple-500/20 transition-all border ${cardBorder}`}>
                  <Eye className={`w-5 h-5 mx-auto mb-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Details</span>
                </button>
                <button className={`${inputBg} rounded-xl p-3 text-center hover:bg-purple-500/20 transition-all border ${cardBorder}`}>
                  <Shield className={`w-5 h-5 mx-auto mb-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Freeze</span>
                </button>
                <button className={`${inputBg} rounded-xl p-3 text-center hover:bg-purple-500/20 transition-all border ${cardBorder}`}>
                  <Plus className={`w-5 h-5 mx-auto mb-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Add to Wallet</span>
                </button>
              </div>
            </div>

            {/* Create Payment Link */}
            <div className={`${cardBg} border ${cardBorder} rounded-2xl p-5`}>
              <div className="flex items-center gap-2 mb-4">
                <Link2 className="w-5 h-5 text-purple-500" />
                <h2 className={`font-semibold ${textPrimary}`}>Payment Link</h2>
              </div>
              
              <p className={`text-sm ${textMuted} mb-4`}>Create a link to receive payments from anyone</p>
              
              <button 
                onClick={() => setShowPaymentLinkModal(true)}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium py-3 rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Create Payment Link
              </button>
            </div>
          </div>

          {/* Column 3: Quick Actions + Crypto Summary */}
          <div className="space-y-4">
            {/* Quick Actions */}
            <div className={`${cardBg} border ${cardBorder} rounded-2xl p-5`}>
              <h2 className={`font-semibold ${textPrimary} mb-4`}>Quick Actions</h2>
              <div className="space-y-2">
                <button 
                  onClick={() => navigate('/banking')}
                  className={`w-full flex items-center justify-between p-3 ${inputBg} rounded-xl hover:bg-purple-500/10 transition-all group`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                      <Plus className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="text-left">
                      <p className={`font-medium ${textPrimary}`}>Add Money</p>
                      <p className={`text-xs ${textMuted}`}>Deposit via card or bank</p>
                    </div>
                  </div>
                  <ChevronRight className={`w-5 h-5 ${textMuted} group-hover:text-purple-500 transition-colors`} />
                </button>

                <button 
                  onClick={() => navigate('/send-money')}
                  className={`w-full flex items-center justify-between p-3 ${inputBg} rounded-xl hover:bg-purple-500/10 transition-all group`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <Send className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="text-left">
                      <p className={`font-medium ${textPrimary}`}>Send Money</p>
                      <p className={`text-xs ${textMuted}`}>Transfer to anyone</p>
                    </div>
                  </div>
                  <ChevronRight className={`w-5 h-5 ${textMuted} group-hover:text-purple-500 transition-colors`} />
                </button>

                <button 
                  onClick={() => navigate('/transactions')}
                  className={`w-full flex items-center justify-between p-3 ${inputBg} rounded-xl hover:bg-purple-500/10 transition-all group`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-purple-500" />
                    </div>
                    <div className="text-left">
                      <p className={`font-medium ${textPrimary}`}>Transactions</p>
                      <p className={`text-xs ${textMuted}`}>View history</p>
                    </div>
                  </div>
                  <ChevronRight className={`w-5 h-5 ${textMuted} group-hover:text-purple-500 transition-colors`} />
                </button>
              </div>
            </div>

            {/* Crypto Wallets Summary */}
            <div className={`${cardBg} border ${cardBorder} rounded-2xl p-5`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-purple-500" />
                  <h2 className={`font-semibold ${textPrimary}`}>Crypto</h2>
                </div>
                <button 
                  onClick={() => navigate('/wallets')}
                  className="text-purple-500 text-sm font-medium hover:text-purple-400 transition-colors"
                >
                  View All
                </button>
              </div>

              <div className="space-y-2">
                {pricesLoading ? (
                  <div className={`text-center py-4 ${textMuted}`}>
                    <div className="animate-spin w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p className="text-xs">Loading prices...</p>
                  </div>
                ) : (cryptoWallets || []).length === 0 ? (
                  <div className={`text-center py-4 ${textMuted}`}>
                    <p className="text-sm mb-2">No wallets yet</p>
                    <button 
                      onClick={() => navigate('/wallets')}
                      className="text-purple-500 text-sm font-medium hover:text-purple-400"
                    >
                      Generate Wallet
                    </button>
                  </div>
                ) : (
                  (cryptoWallets || []).slice(0, 4).map((wallet, index) => {
                    // Map network name to symbol (e.g., 'ethereum' -> 'ETH')
                    const networkName = wallet.network?.toLowerCase();
                    const symbol = wallet.symbol?.toUpperCase() || NETWORK_TO_SYMBOL[networkName] || networkName?.toUpperCase();
                    const priceData = livePrices[symbol];
                    const currentPrice = getCurrentPrice(symbol);
                    const walletValue = getWalletValue(wallet);
                    const priceChange = getPriceChange(symbol);
                    
                    return (
                      <div key={wallet.address || index} className={`flex items-center justify-between p-2 ${inputBg} rounded-xl`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold">
                            {symbol?.substring(0, 2)}
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${textPrimary}`}>{symbol}</p>
                            <div className="flex items-center gap-2">
                              <p className={`text-xs ${textMuted}`}>{(wallet.balance || 0).toFixed(4)}</p>
                              {priceChange !== 0 && (
                                <span className={`text-xs ${priceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {currentPrice > 0 ? (
                            <>
                              <p className={`text-sm font-medium ${textPrimary}`}>
                                ${currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                              <p className={`text-xs ${textMuted}`}>
                                ≈ ${walletValue.toFixed(2)}
                              </p>
                            </>
                          ) : (
                            <p className={`text-sm font-medium ${textMuted}`}>--</p>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Link Modal */}
      {showPaymentLinkModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${cardBg} border ${cardBorder} rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-lg font-bold ${textPrimary}`}>Create Payment Link</h3>
              <button 
                onClick={() => {setShowPaymentLinkModal(false); setGeneratedLink(null);}}
                className={`${textMuted} hover:${textPrimary}`}
              >
                ✕
              </button>
            </div>

            {!generatedLink ? (
              <div className="space-y-4">
                <div>
                  <label className={`text-sm ${textMuted} mb-2 block`}>Amount ({selectedCurrency})</label>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="0.00"
                    className={`w-full ${inputBg} border ${cardBorder} rounded-xl h-12 px-4 ${textPrimary} placeholder:${textMuted} focus:outline-none focus:border-purple-500`}
                  />
                </div>
                <div>
                  <label className={`text-sm ${textMuted} mb-2 block`}>Description (optional)</label>
                  <input
                    type="text"
                    value={paymentDescription}
                    onChange={(e) => setPaymentDescription(e.target.value)}
                    placeholder="What's this payment for?"
                    className={`w-full ${inputBg} border ${cardBorder} rounded-xl h-12 px-4 ${textPrimary} placeholder:${textMuted} focus:outline-none focus:border-purple-500`}
                  />
                </div>
                <button 
                  onClick={generatePaymentLink}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium py-3 rounded-xl hover:opacity-90 transition-all"
                >
                  Generate Link
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="mb-4 p-4 bg-white rounded-xl inline-block">
                  <QRCodeSVG value={generatedLink} size={150} />
                </div>
                <p className={`text-sm ${textMuted} mb-2`}>Payment Link</p>
                <div className={`${inputBg} rounded-xl p-3 flex items-center gap-2 mb-4`}>
                  <p className={`text-xs ${textPrimary} truncate flex-1`}>{generatedLink}</p>
                  <button 
                    onClick={() => copyToClipboard(generatedLink, 'paylink')}
                    className="text-purple-500 hover:text-purple-400"
                  >
                    {copiedField === 'paylink' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className={`text-sm font-medium ${textPrimary} mb-4`}>
                  {currentCurrency.symbol}{paymentAmount} {paymentDescription && `• ${paymentDescription}`}
                </p>

                {/* Accepted Payment Methods */}
                <div className={`${inputBg} rounded-xl p-4 mb-4`}>
                  <p className={`text-xs ${textMuted} mb-3`}>Accepted Payment Methods</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {CARD_NETWORKS.map((network) => (
                      <div 
                        key={network.name}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold"
                        style={{ backgroundColor: network.color, color: network.textColor }}
                      >
                        {network.name}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center gap-4 mt-3">
                    <div className="flex items-center gap-1">
                      <Shield className="w-3 h-3 text-green-500" />
                      <span className={`text-xs ${textMuted}`}>Secure</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CreditCard className="w-3 h-3 text-blue-500" />
                      <span className={`text-xs ${textMuted}`}>3D Secure</span>
                    </div>
                  </div>
                </div>

                {/* Share Options */}
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => {
                      navigator.share?.({ title: 'Payment Link', url: generatedLink }) || copyToClipboard(generatedLink, 'paylink');
                    }}
                    className={`${inputBg} border ${cardBorder} rounded-xl p-3 flex items-center justify-center gap-2 hover:bg-purple-500/10 transition-all`}
                  >
                    <Send className={`w-4 h-4 ${textMuted}`} />
                    <span className={`text-sm ${textPrimary}`}>Share</span>
                  </button>
                  <button 
                    onClick={() => {setGeneratedLink(null); setPaymentAmount(''); setPaymentDescription('');}}
                    className={`${inputBg} border ${cardBorder} rounded-xl p-3 flex items-center justify-center gap-2 hover:bg-purple-500/10 transition-all`}
                  >
                    <Plus className={`w-4 h-4 ${textMuted}`} />
                    <span className={`text-sm ${textPrimary}`}>New Link</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
